import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CartProvider } from "@/contexts/CartContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "next-themes";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import { Suspense, lazy } from "react";
import { usePreloadPages } from "@/hooks/usePreloadPages";
import AppNoIndexLayout from "@/components/layout/AppNoIndexLayout";
import PublicLayout from "@/components/layout/PublicLayout";

// Landing pages - keep direct for SEO + LCP
import Index from "./pages/Index";
import AC01LandingPage from "./pages/AC01LandingPage";
import LP1790 from "./pages/LP1790";
import NumerologiaDoNome from "./pages/NumerologiaDoNome";
import CarrinhoLanding from "./pages/CarrinhoLanding";
import AcompanharPedido from "./pages/AcompanharPedido";
import ObrigadoCompra from "./pages/ObrigadoCompra";

// Dynamic cart page - lazy loaded
const CarrinhoDinamico = lazy(() => import("./pages/CarrinhoDinamico"));
import NotFound from "./pages/NotFound";

// Auth pages - lazy with preload
const Login = lazy(() => import("./pages/Login"));
const EsqueceuSenha = lazy(() => import("./pages/EsqueceuSenha"));
const RedefinirSenha = lazy(() => import("./pages/RedefinirSenha"));
const CriarConta = lazy(() => import("./pages/CriarConta"));
const VerifyMagicLink = lazy(() => import("./pages/VerifyMagicLink"));

// Blog public pages - lazy
const PublicBlog = lazy(() => import("./pages/PublicBlog"));
const PublicBlogPost = lazy(() => import("./pages/PublicBlogPost"));

// Dashboard pages - lazy with preload pós-login
const Home = lazy(() => import("./pages/Home"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Perfil = lazy(() => import("./pages/Perfil"));
const CompletarCadastro = lazy(() => import("./pages/CompletarCadastro"));

// Numerology pages - lazy
const MapaNumerologico = lazy(() => import("./pages/MapaNumerologico"));
const MapaVocacoesProfissionais = lazy(() => import("./pages/MapaVocacoesProfissionais"));
const EstrategiasAbundancia = lazy(() => import("./pages/EstrategiasAbundancia"));
const LandingPage = lazy(() => import("./pages/LandingPage"));

// Commerce pages - lazy
const Consultas = lazy(() => import("./pages/Consultas"));
const CarrinhoCompras = lazy(() => import("./pages/CarrinhoCompras"));
const MeusProdutos = lazy(() => import("./pages/MeusProdutos"));
const MeusPedidos = lazy(() => import("./pages/MeusPedidos"));
const ConfirmacaoPagamento = lazy(() => import("./pages/ConfirmacaoPagamento"));

// Utility pages - lazy
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Comunidade = lazy(() => import("./pages/Comunidade"));
const Agenda = lazy(() => import("./pages/Agenda"));
const Historico = lazy(() => import("./pages/Historico"));
const Suporte = lazy(() => import("./pages/Suporte"));
const Pesquisa = lazy(() => import("./pages/Pesquisa"));
const Cupons = lazy(() => import("./pages/Cupons"));
const GuiaAtivacaoDiaria = lazy(() => import("./pages/GuiaAtivacaoDiaria"));
const Notificacoes = lazy(() => import("./pages/Notificacoes"));

// Admin pages - lazy apenas para admins
const GestaoServicos = lazy(() => import("./pages/admin/GestaoServicos"));
const BlogAdmin = lazy(() => import("./pages/admin/BlogAdmin"));
const BlogEditor = lazy(() => import("./pages/admin/BlogEditor"));
const BlogCategorias = lazy(() => import("./pages/admin/BlogCategorias"));
const Relatorios = lazy(() => import("./pages/admin/Relatorios"));
const RelatorioUsuario = lazy(() => import("./pages/admin/RelatorioUsuario"));
const ConcessoesManuais = lazy(() => import("./pages/admin/ConcessoesManuais"));
const CampanhasAdmin = lazy(() => import("./pages/admin/CampanhasAdmin"));
const CMSLandingPages = lazy(() => import("./pages/admin/CMSLandingPages"));
const LandingPageEditor = lazy(() => import("./pages/admin/LandingPageEditor"));

// Dynamic Landing Page - public
const DynamicLandingPage = lazy(() => import("./pages/DynamicLandingPage"));

// Page skeleton component
const PageSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 animate-pulse">
    <div className="h-16 bg-muted/40 mb-8"></div>
    <div className="container mx-auto px-4 space-y-8">
      <div className="h-8 bg-muted/40 rounded w-1/3"></div>
      <div className="space-y-4">
        <div className="h-4 bg-muted/40 rounded w-3/4"></div>
        <div className="h-4 bg-muted/40 rounded w-1/2"></div>
        <div className="h-4 bg-muted/40 rounded w-2/3"></div>
      </div>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (v5 syntax)
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});
// Force reload to fix import cache issues

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
      >
        <AuthProvider>
          <CartProvider>
            <TooltipProvider>
              <AppContent />
            </TooltipProvider>
          </CartProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

const AppContent = () => {
  usePreloadPages();
  
  return (
    <>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
          <Routes>
            {/* PÚBLICO - Para SEO/ranqueamento */}
            <Route path="/" element={<PublicLayout />}>
              <Route index element={<AC01LandingPage />} />
            </Route>
            <Route path="/numerologia-do-nome" element={<PublicLayout />}>
              <Route index element={<NumerologiaDoNome />} />
            </Route>
            <Route path="/carrinho-landing" element={<PublicLayout />}>
              <Route index element={<CarrinhoLanding />} />
            </Route>
            <Route path="/acompanhar-pedido" element={<PublicLayout />}>
              <Route index element={<AcompanharPedido />} />
            </Route>
            <Route path="/obrigado-compra" element={<PublicLayout />}>
              <Route index element={<ObrigadoCompra />} />
            </Route>
            <Route path="/blog" element={<PublicLayout />}>
              <Route index element={
                <Suspense fallback={<PageSkeleton />}>
                  <PublicBlog />
                </Suspense>
              } />
            </Route>
            <Route path="/blog/:slug" element={<PublicLayout />}>
              <Route index element={
                <Suspense fallback={<PageSkeleton />}>
                  <PublicBlogPost />
                </Suspense>
              } />
            </Route>
            <Route path="/verify-magic-link" element={<PublicLayout />}>
              <Route index element={
                <Suspense fallback={<PageSkeleton />}>
                  <VerifyMagicLink />
                </Suspense>
              } />
            </Route>
            <Route path="/carrinho/:slug" element={<PublicLayout />}>
              <Route index element={
                <Suspense fallback={<PageSkeleton />}>
                  <CarrinhoDinamico />
                </Suspense>
              } />
            </Route>
            
            {/* Landing Page específica 1790 */}
            <Route path="/lp/mapa-numerologico-1790" element={<PublicLayout />}>
              <Route index element={<LP1790 />} />
            </Route>
            
            {/* Dynamic Landing Pages - CMS */}
            <Route path="/lp/:slug" element={<PublicLayout />}>
              <Route index element={
                <Suspense fallback={<PageSkeleton />}>
                  <DynamicLandingPage />
                </Suspense>
              } />
            </Route>
            
            {/* PRIVADO - Área logada com noindex */}
            <Route path="/app" element={<AppNoIndexLayout />}>
              <Route path="loja-de-servicos" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <Index />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="home" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <Home />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="dashboard" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <Dashboard />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="cupons" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <Cupons />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="perfil" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <Perfil />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="mapa-numerologico" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <MapaNumerologico />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="mapa-vocacoes-profissionais" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <MapaVocacoesProfissionais />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="estrategias-abundancia" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <EstrategiasAbundancia />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="mapa-numerologico-02" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <LandingPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="consultas" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <Consultas />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="carrinho" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <CarrinhoCompras />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="completar-cadastro" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <CompletarCadastro />
                  </Suspense>
                </ProtectedRoute>
              } />
              
              {/* Auth routes inside /app */}
              <Route path="login" element={
                <ProtectedRoute requireAuth={false}>
                  <Suspense fallback={<PageSkeleton />}>
                    <Login />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="esqueceu-senha" element={
                <ProtectedRoute requireAuth={false}>
                  <Suspense fallback={<PageSkeleton />}>
                    <EsqueceuSenha />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="redefinir-senha" element={
                <ProtectedRoute requireAuth={false} allowEmailAccess={true}>
                  <Suspense fallback={<PageSkeleton />}>
                    <RedefinirSenha />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="criar-conta" element={
                <ProtectedRoute requireAuth={false}>
                  <Suspense fallback={<PageSkeleton />}>
                    <CriarConta />
                  </Suspense>
                </ProtectedRoute>
              } />
              
              {/* Blog privado */}
              <Route path="blog-interno" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <Blog />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="blog-interno/:slug" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <BlogPost />
                  </Suspense>
                </ProtectedRoute>
              } />
              
              {/* Outras páginas protegidas */}
              <Route path="comunidade" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <Comunidade />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="agenda" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <Agenda />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="historico" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <Historico />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="suporte" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <Suporte />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="meus-produtos" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <MeusProdutos />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="meus-pedidos" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <MeusPedidos />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="pesquisa" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <Pesquisa />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="notificacoes" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <Notificacoes />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/servicos" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <GestaoServicos />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/blog" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <BlogAdmin />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/blog/novo" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <BlogEditor />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/blog/editar/:id" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <BlogEditor />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/blog/categorias" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <BlogCategorias />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/campanhas" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <CampanhasAdmin />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/relatorios" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <Relatorios />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/relatorio-usuario" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <RelatorioUsuario />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/concessoes-manuais" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <ConcessoesManuais />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/cms-landing-pages" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <CMSLandingPages />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/editor-landing-page" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <LandingPageEditor />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="confirmacao-pagamento" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <ConfirmacaoPagamento />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="guia-ativacao-diaria" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <GuiaAtivacaoDiaria />
                  </Suspense>
                </ProtectedRoute>
              } />
            </Route>
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </>
    );
  };

export default App;
